import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Formats a phone number to (XXX) XXX-XXXX format
 * Handles various input formats and returns formatted string or original if invalid
 * @param phoneNumber - The phone number to format (string or null/undefined)
 * @returns Formatted phone number or original input if invalid
 */
export function formatPhoneNumber(phoneNumber: string | null | undefined): string {
  // Return 'N/A' or empty string for null/undefined
  if (!phoneNumber) return 'N/A';

  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Check if we have a valid 10-digit US number (or 11 digits with country code)
  if (cleaned.length === 10) {
    // Format: (XXX) XXX-XXXX
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // Format with country code: +1 (XXX) XXX-XXXX
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if not a valid format
  return phoneNumber;
}

/**
 * Formats a phone number as user types (for input fields)
 * Automatically adds formatting as the user types
 * @param value - The current input value
 * @returns Formatted phone number for display in input
 */
export function formatPhoneNumberInput(value: string): string {
  // Remove all non-numeric characters
  const cleaned = value.replace(/\D/g, '');

  // Apply formatting based on length
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
}

/**
 * Removes formatting from a phone number to get raw digits
 * Useful for saving to database or API calls
 * @param formattedNumber - The formatted phone number
 * @returns Raw numeric string
 */
export function unformatPhoneNumber(formattedNumber: string | null | undefined): string {
  if (!formattedNumber) return '';
  return formattedNumber.replace(/\D/g, '');
}